import { useState } from 'react';
import { ZodError } from 'zod';
import { Box, Button, Typography } from '@mui/material';
import { useToast } from '../../components/common/ToasterContext';
import CreateMemberComponent from '../../components/Member/CreateMember';
import { FormValues } from '../../components/Member/CreateMember/types';
import getAPIUrl from '../../config';
import { parseError, postApiWithAuth } from '../../utils/apiCalls';
import { CreateMemberContainerProps } from './types';
import { createMemberSchema } from '../../utils/schemas';
import { transformPhoneForAPI, trimFormData } from '../../utils/helpers';
import Popup from '../../components/common/Popup/Popup';
import styles from './styles';

const CreateMember = ({
    createMember,
    setCreateMember,
    setReload,
    fromSelectedPartner,
    partnerData,
    selectedPartner,
    reload,
}: CreateMemberContainerProps) => {
    const defaultValues = {
        firstname: '',
        lastname: '',
        email: '',
        selectedPartnerId: (fromSelectedPartner ? selectedPartner?.id : partnerData[0]?.id) || partnerData[0]?.id,
        employerId:
            (fromSelectedPartner ? selectedPartner?.uniqueId : partnerData[0]?.uniqueId) || partnerData[0]?.uniqueId,
        needs_welcome_kit: '1',
        phone: '',
        dateOfBirth: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
    };
    const [formValues, setFormValues] = useState<FormValues>(defaultValues);
    const toast = useToast();
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const [showExistingEmailPopup, setShowExistingEmailPopup] = useState(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            createMemberSchema.parse(trimFormData(formValues));
            await postApiWithAuth(
                `${getAPIUrl()}/employers/create-member/${formValues.employerId}`,
                {
                    first_name: formValues.firstname,
                    last_name: formValues.lastname,
                    email: formValues.email,
                    date_of_birth: formValues.dateOfBirth,
                    phone: formValues.phone.replaceAll('-', ''),
                    address_1: formValues.address1,
                    unit_number: formValues.address2,
                    city: formValues.city,
                    stateregion: formValues.state,
                    postal_code: formValues.zip,
                },
                {
                    needs_welcome_kit: formValues.needs_welcome_kit,
                },
            );
            setFormValues(defaultValues);
            setFormErrors(undefined);
            if (setReload) {
                setReload(!reload);
            }
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            setCreateMember(false);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                const err = parseError(error);
                if (err) {
                    if (err === 'Account with email exists, please sign in or use the forgot password flow.') {
                        setShowExistingEmailPopup(true);
                    } else if (err === 'Employer Id should be 8 characters') {
                        toast.onToggleSnackbar();
                        toast.onMessage('Partner should not be empty.');
                        toast.onError();
                    } else {
                        toast.onToggleSnackbar();
                        toast.onMessage(err);
                        toast.onError();
                    }
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        switch (name) {
            case 'phone':
                setFormValues({
                    ...formValues,
                    [name]: transformPhoneForAPI(value),
                });
                break;
            case 'state':
                setFormValues({
                    ...formValues,
                    [name]: value as unknown as string,
                });
                break;
            default:
                setFormValues({
                    ...formValues,
                    [name]: value,
                });
        }
    };

    const handleCloseCreatePop = () => {
        setFormValues(defaultValues);
        setFormErrors(undefined);
        setCreateMember(false);
        setShowExistingEmailPopup(false);
    };

    const handleKitNeedChange = (value: string) => {
        setFormValues({
            ...formValues,
            needs_welcome_kit: value,
        });
    };

    return (
        <>
            <CreateMemberComponent
                open={createMember}
                handleInput={handleInput}
                handleSubmit={onSubmit}
                handleCloseCreatePop={handleCloseCreatePop}
                fromSelectedPartner={fromSelectedPartner}
                formValues={formValues}
                formErrors={formErrors}
                partnerData={partnerData}
                defaultSelectedPartner={fromSelectedPartner ? selectedPartner : partnerData[0]}
                setFormValues={setFormValues}
                onKitNeedChange={handleKitNeedChange}
                submitting={submitting}
            />
            {showExistingEmailPopup && (
                <Popup
                    id="existingMembers"
                    open={showExistingEmailPopup}
                    title="Accounts In Use"
                    textAlign="left"
                    handleClose={() => null}
                    description={<>The following member emails are already in use:</>}
                >
                    <Box sx={styles.errorsDataContainer}>
                        <Typography sx={styles.email}>{formValues?.email}</Typography>
                        <Box sx={styles.popupButtonsContainer}>
                            <Button id="exit" variant="outlined" sx={styles.exit} onClick={handleCloseCreatePop}>
                                Exit
                            </Button>
                            <Button
                                id="tryAgain"
                                variant="contained"
                                sx={styles.tryAgain}
                                onClick={() => setShowExistingEmailPopup(false)}
                            >
                                Try Again
                            </Button>
                        </Box>
                    </Box>
                </Popup>
            )}
        </>
    );
};
export default CreateMember;
